import { Add, Delete, Edit, FilterList, RemoveRedEye } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import {
  IWorkshop,
  IWorkshopCancellationReasons,
  IWorkshopInput,
  WorkshopStatus,
} from "models/workshop";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import BlockIcon from "@mui/icons-material/Block";
import { getCompanyProficiencyLevels } from "framework/companyProficiencyLevel";
import { getTrainers } from "framework/trainer";
import {
  TrainerWorkshopCells,
  TrainerWorkshopCellsExport,
  WorkshopCells,
  WorkshopCellsExport,
  addWorkshop,
  deleteWorkshop,
  editWorkshop,
  formatExcellValue,
  getCancellationReasons,
  getWorkshops
} from "framework/workshops";
import { getPermission, getUserData } from "helpers";
import { handleError } from "helpers/utils";
import useFetchSection from "hooks/useFetchSection";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { UserRoleName, UserType } from "types/auth";
import AddWorkshop from "./partials/AddWorkshop/AddWorkshop";
import DeleteWorkshop from "./partials/DeleteWorkshop/DeleteWorkshop";
import EditWorkshop from "./partials/EditWorkshop/EditWorkshop";
import FilterListModal from "./partials/FilterListModal";
import dayjs from "dayjs";
import { ICompany } from "models/company";
import { getCompanyTopics } from "framework/companyTopics";
import { getCompanyCompetencies } from "framework/companyCompetencies";
import { ICompanyCompetency } from "models/companyCompetencies";
import { ICompanyTopic } from "models/companyTopics";


interface Props {
  feature: Feature;
}

interface _Filter {
  vendors?: any;
  venues?: any;
  modalities?: any;
  trainers?: any;
  topics?: any;
  companies?: any;
  cities?: any;
  countries?: any;
  competecnies?: any;
  profLevel?: any;
  cancellationReasons?: IWorkshopCancellationReasons[];
  languages?: any
}

const TrainerWorkshops: React.FC<Props> = ({ feature }) => {
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterWorkshops, setFilterWorkshops] = useState<IWorkshop[]>(
    [] as IWorkshop[]
  );
  const [filters, setFilters] = useState<_Filter>({});
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [competencies, setCompetencies] = useState<ICompanyCompetency[]>([]);
  const [topics, setTopics] = useState<ICompanyTopic[]>([]);
  const [selectedWorkshop, setSelectedWorkshop] = useState<IWorkshop>(
    {} as IWorkshop
  );
  const filtersRef = useRef<any>(null);
  const userData = getUserData();
  const { colors, user, roleName } = userData;
  const userType = user.userType;
  const [inputObj, setInputObj] = useState<IWorkshopInput>(
    {} as IWorkshopInput
  );
  const { setAlert } = useAlert();

  const now = new Date();
  const year = now.getFullYear();


  const [openFilter, setOpenFilter] = useState(false);
  const [filterState, setFilterState] = useState({
    department: "",
    provider: "",
    vendors: "",
    venues: "",
    topic: "",
    modalities: "",
    countries: "",
    cities: "",
    languages: "",
    trainers: "",
    startDate: new Date(year, 0, 1),
    endDate: new Date(year, 11, 31),
  });
  // eslint-disable-next-line
  const queryClient = new QueryClient();


  const editWorkshopsMutation = useMutation({
    mutationFn: (data: { createInput: IWorkshopInput; id: string }) => {
      return editWorkshop(data.createInput, selectedWorkshop.workshopId);
    },
  });

  const deleteWorkshopsMutation = useMutation({
    mutationFn: (code: string) => {
      return deleteWorkshop(code);
    },
  });

  const {
    loading,
    data: Workshops,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getWorkshops(),
    fetchPermission: () => getPermission(6),
    section: "Workshops",
    onSuccess: (data) => {
      setFilterWorkshops(data)
    },
    processResponse: (data) =>
      data.map((item) => {
        return { ...item, requiresInvitationStr: item.requiresInvitation ? 'Yes' : 'No' };
      }),
  });

  // get filters
  const fetchFilters = async () => {
    try {
      const [
        trainersResponse,
        profLevelResponse,
        cancellationReasonsResponse,
        filtersResponse,
      ] = await Promise.all([

        queryClient.fetchQuery("get Trainers", () => getTrainers()),
        queryClient.fetchQuery("get Prof level", () =>
          getCompanyProficiencyLevels(selectedWorkshop ? `?workshopCompanyId=${selectedWorkshop.companyId || ''}` : '')
        ),
        queryClient.fetchQuery("get Cancellation Reasons", () =>
          getCancellationReasons()
        ),
        queryClient.fetchQuery("getWorkshopFilters", () => getWorkshops(`/filters`))
      ]);

      setFilters({
        trainers: trainersResponse,
        profLevel: profLevelResponse,
        cancellationReasons: cancellationReasonsResponse,
        ...filtersResponse
      });
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    }
  };

  const fetchCompetencies = useCallback(async () => {
    if (!selectedWorkshop.companyId) return
    setLoading2(true);
    try {
      const res = await queryClient.fetchQuery("get company competencies by one", () => getCompanyCompetencies(`?workshopCompanyId=${selectedWorkshop.companyId}`));
      setCompetencies(res);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);


  const fetchTopics = useCallback(async () => {
    if (!selectedWorkshop.companyId) return
    setLoading2(true);
    try {
      const res = await queryClient.fetchQuery("get company topics by one", () => getCompanyTopics(`?workshopCompanyId=${selectedWorkshop.companyId}`));
      setTopics(res);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);


  useEffect(() => {
    fetchCompetencies()
    // fetchTopics()
  }, [selectedWorkshop])

  useEffect(() => {
    fetchFilters();

    // eslint-disable-next-line
  }, [selectedWorkshop]);

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name === "competencies") {
      let competencies = value
        .map((competency: any) => competency.value)
        .join(";");
      setInputObj((prev) => ({
        ...prev,
        [name]: competencies,
      }));
    }

    if (name.includes('Time')) {

      const date = dayjs(value);


      const time = `${String(date.hour()).padStart(2, '0')}:${String(date.minute()).padStart(2, '0')}:00`;

      setInputObj((prev) => ({
        ...prev,
        [name]: time,
      }));
    }

    else if (name === "languages") {
      let languages = value
        .map((language: any) => ({ Id: language.value }))

      setInputObj((prev) => ({
        ...prev,
        [name]: languages,
      }));
    }
    else if (name === "requires_invitation") {
      setInputObj((prev) => ({
        ...prev,
        [name]: value || false,
      }));
    }

    else {
      setInputObj((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };


  const handleOpenEdit = (Workshop: IWorkshop) => {
    setSelectedWorkshop(Workshop);
    setInputObj({modality: Workshop.modality} as IWorkshopInput)
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedWorkshop({} as IWorkshop);
    setInputObj({} as IWorkshopInput)
  };

  const handleOpenDelete = (Workshop: IWorkshop) => {
    setSelectedWorkshop(Workshop);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedWorkshop({} as IWorkshop);
    setInputObj({} as IWorkshopInput)
  };

  const getBodyCells = (row: IWorkshop) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "companyName")
        return {
          id: "companyName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.companyName : row.companyNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "vendorName")
        return {
          id: "vendorName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.vendorName : row.vendorNameAr}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "venueName")
        return {
          id: "venueName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.venueName : row.venueNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "trainer")
        return {
          id: "trainer",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.trainer : row.trainerNameAr}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "topicName")
        return {
          id: "topicName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.topicName : row.topicNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "roomNumber")
        return {
          id: "roomNumber",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.roomNumber}</Typography>
            </TableCell>
          ),
        };
      if (key === "capacity")
        return {
          id: "capacity",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.capacity}</Typography>
            </TableCell>
          ),
        };
      if (key === "languages")
        return {
          id: "languages",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.languages.map(a => a.name).join(',')}</Typography>
            </TableCell>
          ),
        };

      if (key === "link")
        return {
          id: "link",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.link}</Typography>
            </TableCell>
          ),
        };

      if (key === "companies")
        return {
          id: "companies",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row!?.companies!?.map(a => filters.companies.find((item: ICompany) => item?.id === a)?.name).join(',')}</Typography>
            </TableCell>
          ),
        };

      if (key === "fromDate")
        return {
          id: "fromDate",
          element: (
            <TableCell align={"left"} key={i}>
              {row.fromDate && (
                <Typography variant="body2">
                  {row.fromDateDisplay}
                </Typography>
              )}
            </TableCell>
          ),
        };
      if (key === "toDate")
        return {
          id: "toDate",
          element: (
            <TableCell align={"left"} key={i}>
              {row.toDate && (
                <Typography variant="body2">
                  {row.toDateDisplay}
                </Typography>
              )}
            </TableCell>
          ),
        };

      if (key === "fromTime") {
        const from = row.fromTimeDisplay || row.fromTime || "";
        const to = row.toTimeDisplay || row.toTime || "";
        const range = [from, to].filter(Boolean).join(" - " );

        return {
          id: "fromTime",
          element: (
            <TableCell align={"left"} key={i}>
              {range && (
                <Typography variant="body2">
                  {range}
                </Typography>
              )}
            </TableCell>
          ),
        };
      }
      if (key === "toTime") return undefined;

      if (key === "averagePreScore")
        return {
          id: "averagePreScore",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.averagePreScore.toString()}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "averagePostScore")
        return {
          id: "averagePostScore",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.averagePostScore.toString()}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "requiresInvitation")
        return {
          id: "requiresInvitation",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.requiresInvitationStr}
              </Typography>
            </TableCell>
          ),
        };
          if (key === "coHost") return undefined;
      if (key === "prerequisite")
        return {
          id: "prerequisite",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.prerequisite}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "attendanceFrequency")
        return {
          id: "attendanceFrequency",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.attendanceFrequency}
              </Typography>
            </TableCell>
          ),
        };

        if (key === "numberOfEnrollments")
                        return {
                          id: "numberOfEnrollments",
                          element: (
                            <TableCell align={"left"} key={i}>
                              <Typography variant="body2">{row.numberOfEnrollments}</Typography>
                            </TableCell>
                          ),
                        };
                
                        if (key === "pretestCount")
                        return {
                          id: "pretestCount",
                          element: (
                            <TableCell align={"left"} key={i}>
                              <Typography variant="body2">{row.pretestCount}</Typography>
                            </TableCell>
                          ),
                        };
                
                        if (key === "posttestCount")
                        return {
                          id: "posttestCount",
                          element: (
                            <TableCell align={"left"} key={i}>
                              <Typography variant="body2">{row.posttestCount}</Typography>
                            </TableCell>
                          ),
                        };
        
                        if (key === "evaluationCount")
                        return {
                          id: "evaluationCount",
                          element: (
                            <TableCell align={"left"} key={i}>
                              <Typography variant="body2">{row.evaluationCount}</Typography>
                            </TableCell>
                          ),
                        };
                        
      if (key === "workshopId")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {userType !== UserType.VENDOR_USER &&
                  permission?.hasDeletePermission && (
                    <Tooltip title={t("T_delete")}>
                      <IconButton
                        size={"small"}
                        sx={{
                          color: colors.deleteIconColor
                            ? colors.deleteIconColor
                            : "red",
                          "&:hover": { color: colors?.deleteIconHoverColor },
                        }}
                        onClick={() => handleOpenDelete(row)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  )}

                <Tooltip title={t("T_details")}>
                  <Link to={`${row.workshopId}`}>
                    <IconButton size={"small"} color={"info"}>
                      <RemoveRedEye
                        sx={{
                          color: colors.viewIconColor
                            ? colors.viewIconColor
                            : "#0288d1",
                          "&:hover": { color: colors?.viewIconHoverColor },
                        }}
                      />
                    </IconButton>
                  </Link>
                </Tooltip>
              </Stack>
            </TableCell>
          ),
        };
      else return <></>;
    });

    return cells;
  };


  const handleFilterChange = (key: string, value: string) => {
    setFilterState((prev) => ({ ...prev, [key]: value }));
    makeRequest({ ...filterState, [key]: value });
  };

  const makeRequest = (filterData: typeof filterState) => {
    let filtered = Workshops!;
    const { vendors, venues,trainers, provider, topic, modalities, countries, cities, languages } = filterData;

    if (provider) filtered = filtered.filter((w) => w.companyId === provider);
    if (topic) filtered = filtered.filter((w) => w.topicId === topic);
    if (trainers) filtered = filtered.filter((w) => w.trainerId === trainers);
    if (modalities) filtered = filtered.filter((w) => w.modality === +modalities);
    if (venues) filtered = filtered.filter((w) => w.venueId === venues);
    if (vendors) filtered = filtered.filter((w) => w.vendorId === vendors);
    if (countries) filtered = filtered.filter((w) => w.countryId === countries);
    if (cities) filtered = filtered.filter((w) => w.cityId === cities);
    if (languages) filtered = filtered.filter((w) => w.languages.find((l) => l.id === languages));
    if (filterData.startDate) filtered = filtered.filter((w) => new Date(w.fromDate) >= filterData.startDate);
    if (filterData.endDate) filtered = filtered.filter((w) => new Date(w.toDate) <= filterData.endDate);

    setFilterWorkshops(filtered);
  };




  const resetFilters = () => {
    if (filtersRef.current) {
      filtersRef.current.resetFilters(); // Call the reset function from Filters component
    }
    makeRequest({
      department: "",
      provider: "",
      vendors: "",
      venues: "",
      topic: "",
      modalities: "",
      countries: "",
      trainers: "",
      cities: "",
      languages: "",
      startDate: new Date(year, 0, 1),
      endDate: new Date(year, 11, 31),
    });
    setFilterState({
      department: "",
      provider: "",
      vendors: "",
      venues: "",
      topic: "",
      modalities: "",
      countries: "",
      trainers: "",
      cities: "",
      languages: "",
      startDate: new Date(year, 0, 1),
      endDate: new Date(year, 11, 31),
    })
  };


  const handleEdit = async (data: any[]) => {
    try {
      setLoading2(true);
      let createInput: any = {
        prerequisite: inputObj.prerequisite ?? selectedWorkshop.prerequisite,
        topicCompetencies: data,
      };
      await editWorkshopsMutation.mutateAsync({
        createInput,
        id: selectedWorkshop.workshopId,
      });
      setAlert({
        open: true,
        message: "Workshop updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteWorkshopsMutation.mutateAsync(selectedWorkshop.workshopId);
      setAlert({
        open: true,
        message: "Workshop deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };



  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            spacing={1}
            width={"100%"}
            marginBottom={5}
          >
            <Typography
              variant="h3"
              color={colors?.labelColor ? colors?.labelColor : "header.primary"}
            >
              {t(feature.name)}
            </Typography>
            <Stack direction={"row"} spacing={1}>
              <Tooltip title={t('T_filter_workshops')}>
                <IconButton onClick={() => setOpenFilter(true)}>
                  <FilterList />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterWorkshops.length > 0 && (
          <List
            headCells={TrainerWorkshopCells}
            data={FilterWorkshops}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
            formatExcelValue={formatExcellValue}
            exportHeaders={TrainerWorkshopCellsExport}
          />
        )}
        {(!FilterWorkshops || FilterWorkshops.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_workshop_header")}
              message={t("T_no_workshop_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openEdit && filters && (
        <EditWorkshop
        inputObj={inputObj}
          open={openEdit}
          loading={loading2}
          vendors={filters.vendors}
          workshopEvaluations={[]}
          venues={filters.venues}
          countries={filters.countries}
          cities={filters.cities}
          trainers={filters.trainers}
          topics={topics || []}
          profLevel={filters.profLevel}
          modalities={filters.modalities}
          competencies={competencies || []}
          workshop={selectedWorkshop}
          languages={filters.languages}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteWorkshop
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}

      {openFilter && (
        <FilterListModal
          open={openFilter}
          filters={filters}
          {...filterState}
          onClose={() => setOpenFilter(false)}
          onReset={resetFilters}
          onChangeCities={(e: any) => handleFilterChange("cities", e)}
          onChangeCountries={(e: any) => handleFilterChange("countries", e)}
          onChangeLanguages={(e: any) => handleFilterChange("languages", e)}
          onChangeModalities={(e: any) => handleFilterChange("modalities", e)}
          onChangeTopic={(e: any) => handleFilterChange("topic", e)}
          onChangeTrainer={(e: any) => handleFilterChange("trainers", e)}
          onChangeVenues={(e: any) => handleFilterChange("venues", e)}
          onChangeVendor={(e: any) => handleFilterChange("vendors", e)}
          onChangeStartDate={(e: any) => handleFilterChange("startDate", e)}
          onChangeEndDate={(e: any) => handleFilterChange("endDate", e)}
        />
      )}
    </>
  );
};

export default TrainerWorkshops;
